import { css } from '@emotion/react';
import styled from '@emotion/styled';
import React, { forwardRef, RefObject } from 'react';
import { Typography } from '../../typography/Typography';
import { FormError } from '../formError/FormError';
import tokens from './tokens';

export interface OptionBaseProps {
  label?: string | React.ReactNode;
  id: string;
  checked?: boolean;
  defaultChecked?: boolean;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  value?: string;
  disabled?: boolean;
  required?: boolean;
  invalid?: boolean;
  valid?: boolean;
  type?: 'checkbox' | 'radio';
  name?: string;
  hideLabel?: boolean;
  ariaLabel?: string;
  errorMessage?: string;
}

type StyledOptionBaseProps = Pick<
  OptionBaseProps,
  'invalid' | 'disabled' | 'valid' | 'hideLabel' | 'ariaLabel'
>;

// Helper function to generate background image based on tickFill token
const getTickFill = () => `url("${tokens.tickFill}")`;

const StyledOptionBase = styled.div<StyledOptionBaseProps>`
  ${({
    theme: { space, colors, borders, borderRadii },
    invalid,
    disabled,
    valid,
    hideLabel,
  }) => css`
    display: flex;
    flex-direction: column;
    gap: ${space.small};

    label,
    input {
      cursor: pointer;
    }

    label {
      user-select: none;
      display: flex;
      align-items: flex-start;
      position: relative;
      gap: ${space.small};
      margin-top: ${tokens.labelTopMargin};

      &:focus {
        border-width: 4px;
        border: ${borders.focus};
        border-radius: ${borderRadii.medium};
      }

      &:before {
        content: '';
        width: ${space[tokens.size]};
        height: ${space[tokens.size]};
        border: ${borders.default};
        background: ${colors.utilityWhite};
        transition: all 0.2s ease;
        border-radius: ${borderRadii[tokens.borderRadius]};
        flex-shrink: 0;
        position: relative;
        top: ${tokens.labelBeforeTop};

        ${valid &&
        css`
          border: ${borders.valid};
        `}
        ${invalid &&
        css`
          border: ${borders.error};
        `}
        ${disabled &&
        css`
          border: ${borders.disabled};
        `}
      }

      &:after {
        content: '';
        width: ${tokens.tickWidth};
        height: ${tokens.tickHeight};
        background-image: ${getTickFill(tokens.tickFill)};
        position: absolute;
        left: 4px;
        opacity: 0;
        transform: scale(0);
        transition: all 0.2s ease;
        top: ${tokens.labelAfterTop};
      }
    }

    input {
      position: absolute;
      opacity: 0;
      cursor: pointer;
      height: 0;
      width: 0;

      &:checked + label {
        &:before {
          background: ${colors[tokens.checkedBackground]};
          border: ${borders.valid};
        }

        &:after {
          opacity: 1;
          transform: scale(1);
        }
      }

      &:focus-visible + label {
        &:before {
          /* border-width: 4px; */
          box-shadow: ${borders.focus};
        }
      }
    }

    // Invalid state
    ${invalid &&
    css`
      accent-color: ${colors.systemError};

      label {
        &:before {
          color: ${colors.systemError};
        }
      }

      &:focus-visible + label {
        &:before {
          ${tokens.focusInvalidBoxShadow
            ? `box-shadow: ${tokens.focusInvalidBoxShadow}`
            : `box-shadow: 0 0 0 1px ${colors.systemError}`};
        }
      }
    `}

    // Disabled state
    ${disabled &&
    css`
      opacity: 0.6;
      cursor: not-allowed;
    `}

    // Hide label
    ${hideLabel &&
    css`
      label:not(:focus):not(:active) {
        clip: rect(0 0 0 0);
        clip-path: inset(50%);
        height: 1px;
        overflow: hidden;
        position: absolute;
        white-space: nowrap;
        width: 1px;
      }
    `}
  `}
`;

export const OptionBase = forwardRef<HTMLInputElement, OptionBaseProps>(
  (
    {
      label,
      id,
      checked,
      defaultChecked,
      disabled,
      value,
      onChange,
      name,
      invalid,
      valid,
      type = 'checkbox',
      hideLabel,
      ariaLabel,
      errorMessage,
      ...rest
    },
    ref
  ) => (
    <StyledOptionBase
      invalid={invalid}
      disabled={disabled}
      valid={valid}
      hideLabel={hideLabel}
      ariaLabel={ariaLabel}
      {...rest}
    >
      <input
        type={type}
        id={id}
        name={name || id}
        checked={checked}
        disabled={disabled}
        value={value}
        ref={ref as RefObject<HTMLInputElement>}
        onChange={onChange}
        defaultChecked={defaultChecked}
        aria-label={ariaLabel}
      />
      <Typography
        component="label"
        htmlFor={id}
        id={`${id}-label`}
        variant="bodyMedium"
      >
        {label || ''}
      </Typography>
      {invalid && (
        <FormError
          display={!!invalid && !!errorMessage}
          message={errorMessage || 'Required'}
        />
      )}
    </StyledOptionBase>
  )
);

OptionBase.displayName = 'OptionBase';
